import { useCallback, useEffect, useState } from "react";
import { IndianRupee, X, CheckCircle2 } from "lucide-react";
import api, { inr, fmtDate, fmtErr } from "../../lib/api";
import { toast } from "sonner";

export default function AdminCod() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/cod/dashboard").then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const settle = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/cod/${target.awb}/settle`, { settled_amount: Number(amount), note });
      toast.success(`COD settled for ${target.awb}`);
      setTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const s = data?.summary;

  return (
    <div data-testid="admin-cod-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">COD Settlements</h1>
      <p className="text-sm text-slate-500">Courier COD remittance reconciliation across all customers — settle each AWB when the courier pays out.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[
          ["Expected (in transit)", s?.expected_in_transit, "text-shipany-blue"],
          ["Collected (delivered)", s?.collected_delivered, "text-emerald-600"],
          ["Pending settlement", s?.pending_settlement, "text-amber-600"],
          ["Settled", s?.settled, "text-slate-700"],
          ["Overdue", s?.overdue, "text-red-600"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-cod-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
            <p className={`mono mt-2 text-xl font-bold ${cls}`}>{typeof val === "number" ? inr(val) : "—"}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-cod-table">
        <table className="w-full min-w-[980px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">AWB</th><th className="px-4 py-3.5">Customer</th><th className="px-4 py-3.5">Courier</th>
              <th className="px-4 py-3.5">COD Amount</th><th className="px-4 py-3.5">Expected</th><th className="px-4 py-3.5">Settled</th>
              <th className="px-4 py-3.5">Difference</th><th className="px-4 py-3.5">Status</th><th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.reconciliation || []).map((r) => (
              <tr key={r.settlement_id} className="border-t border-slate-100" data-testid={`admin-cod-row-${r.awb}`}>
                <td className="mono px-5 py-3 font-semibold text-shipany-blue">{r.awb}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.customer_email || r.user_id}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.courier_name}</td>
                <td className="mono px-4 py-3 font-semibold">{inr(r.amount)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(r.expected_date)}</td>
                <td className="mono px-4 py-3">{r.settled_amount != null ? inr(r.settled_amount) : "—"}</td>
                <td className={`mono px-4 py-3 ${r.difference ? (r.difference < 0 ? "text-red-500" : "text-emerald-600") : "text-slate-400"}`}>{r.difference != null ? inr(r.difference) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${r.status === "settled" ? "bg-emerald-50 text-emerald-600" : r.overdue ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                    {r.status === "settled" ? "Settled" : r.overdue ? "Overdue" : "Pending"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status !== "settled" && (
                    <button onClick={() => { setTarget(r); setAmount(String(r.amount)); setNote(""); }} data-testid={`settle-${r.awb}`}
                      className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">
                      <IndianRupee className="h-3.5 w-3.5" /> Settle
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data && data.reconciliation.length === 0 && <tr><td colSpan={9} className="px-6 py-10 text-center text-slate-400">No COD settlements yet — they appear when COD shipments are delivered.</td></tr>}
          </tbody>
        </table>
      </div>

      {target && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cod-settle-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setTarget(null)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Settle {target.awb}</h2>
              <button onClick={() => setTarget(null)} data-testid="cod-settle-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">Customer {target.customer_email || target.user_id} · courier {target.courier_name} · expected {inr(target.amount)}. Enter the amount the courier actually remitted; the difference is tracked.</p>
            <div className="mt-4 space-y-3.5">
              <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cod-settle-amount"
                className="mono w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" placeholder="Settled amount (₹)" />
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cod-settle-note"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none" placeholder="Note (optional — e.g. courier short-paid)" />
            </div>
            <button onClick={settle} disabled={busy} data-testid="cod-settle-confirm"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
              <CheckCircle2 className="h-4 w-4" /> {busy ? "Settling…" : `Mark Settled — ${inr(Number(amount) || 0)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
